import React, { useState } from "react";
import type { Chemical, ChemicalType, ChemicalVendor } from "../../types";
import { CHEMICAL_MANAGEMENT_TYPE, MESSAGES } from "../../constants/const";
import { showErrorMessage } from "../appPane";
import InputText from "../inputText";
import InputRealNumberText from "../inputRealNumber";
import ModalHeader from "./modalHeader";
import Select from "react-select";
import tankIcon from "../../image/tank1.png";
import boxIcon from "../../image/box1.png";
import bagIcon from "../../image/bag1.png";
import canIcon from "../../image/can1.png";
import cylinderIcon from "../../image/cylinder1.png";

interface Props {
  /** 薬剤情報 */
  chemical?: Chemical;
  /** 薬剤販売業者リスト */
  vendors: ChemicalVendor[];
  /** 薬剤カテゴリリスト */
  types: ChemicalType[];
  /** キャンセルボタンハンドラ */
  onClose: () => void;
  /** 登録ボタンハンドラ */
  onRegister: (chemical: Chemical) => void;
}

type IconOptions = {
  key: number;
  value: string;
  label: string;
  icon: string;
};

// アイコンイメージ選択肢
const iconOptions = [
  { key: 1, value: "tank", label: "タンク", icon: tankIcon },
  { key: 2, value: "box", label: "箱", icon: boxIcon },
  { key: 3, value: "bag", label: "袋", icon: bagIcon },
  { key: 4, value: "can", label: "缶", icon: canIcon },
  { key: 5, value: "cylinder", label: "ボンベ", icon: cylinderIcon },
];

const legendStyle = {
  borderBottom: "3px solid var(--color-app-sub1)",
  width: "fit-content",
  boxShadow: "0 3px 0 var(--color-app-main)",
  letterSpacing: "0.05em",
  color: "#333",
  padding: "0 4px 0 4px",
};

const fieldsetStyle = {
  marginBottom: "32px",
};

/**
 * 薬剤編集ポップアップ
 * @param props
 * @returns
 */
export const ChemicalModal: React.FC<Props> = ({
  chemical,
  vendors,
  types,
  onClose,
  onRegister,
}) => {
  // 入力フォームの値
  const [vendorId, setVendorId] = useState(
    vendors.find(v => v.vendorId === chemical?.vendorId)?.vendorId ?? vendors[0]?.vendorId
  );
  const [chemicalTypeId, setChemicalTypeId] = useState(
    types.find(t => t.chemicalTypeId === chemical?.chemicalTypeId)?.chemicalTypeId ??
      types[0]?.chemicalTypeId
  );

    // 追加の販売業者IDの初期値はchemicalのadditionalVendorIdから取得
    const initialAdditionalVendorIds = (chemical?.additionalVendorId ?? []).filter(
      (id): id is number => typeof id === "number"
    );

  // 追加の販売業者IDのstate（最大4つまで）
  const [additionalVendorIds, setAdditionalVendorIds] = useState<number[]>(
    initialAdditionalVendorIds
  );

  // vendorIdとadditionalVendorIdsを合わせた、現在選択されている販売業者IDのリスト
  const selectedVendorIds = [vendorId, ...additionalVendorIds].filter(
    (id): id is number => typeof id === "number"
  );

  // vendorIdがnumber型でないものを除外したリストを作成
  const normalizedVendors = vendors.filter(
    (vendor): vendor is ChemicalVendor & { vendorId: number } =>
      typeof vendor.vendorId === "number"
  );

  // 現在選択されている販売業者IDを除き、かつ追加で選択されていない販売業者を選択肢として表示
  const getVendorOptions = (currentVendorId?: number) => {
    return normalizedVendors.filter(
      vendor =>
        vendor.vendorId === currentVendorId ||
        !selectedVendorIds.includes(vendor.vendorId)
    );
  };

  // 追加の販売業者を選択できるのは、現在選択されている販売業者IDが4つ未満で、かつ選択肢に表示する販売業者が存在する場合
  const canAddVendor = additionalVendorIds.length < 4 && getVendorOptions().length > 0;

  // 販売業者を追加する関数
  const addVendor = () => {
    if (!canAddVendor) return;
    const nextVendor = getVendorOptions().find(v => !selectedVendorIds.includes(v.vendorId));
    if (!nextVendor) return;
    setAdditionalVendorIds(prev => [...prev, nextVendor.vendorId]);
  };

  // 追加した販売業者を削除する関数
  const removeVendor = (index: number) => {
    setAdditionalVendorIds(prev => prev.filter((_, idx) => idx !== index));
  };

  const [chemicalName, setChemicalName] = useState(chemical?.chemicalName ?? "");
  const [chemicalName1, setChemicalName1] = useState(chemical?.chemicalName1 ?? "");
  const [chemicalName2, setChemicalName2] = useState(chemical?.chemicalName2 ?? "");
  const [chemicalName3, setChemicalName3] = useState(chemical?.chemicalName3 ?? "");
  const [chemicalSort1, setChemicalSort1] = useState(chemical?.chemicalSort1?.toString() ?? "");
  const [chemicalSort2, setChemicalSort2] = useState(chemical?.chemicalSort2?.toString() ?? "");
  const [chemicalSort3, setChemicalSort3] = useState(chemical?.chemicalSort3?.toString() ?? "");
  const [chemicalCode, setChemicalCode] = useState(chemical?.chemicalCode ?? "");
  const [chemicalWbs, setChemicalWbs] = useState(chemical?.chemicalWbs ?? "");
  const [chemicalUnit1, setChemicalUnit1] = useState(chemical?.chemicalUnit1 ?? "");
  const [chemicalUnit2, setChemicalUnit2] = useState(chemical?.chemicalUnit2 ?? "");
  const [chemicalUnit2Calc, setChemicalUnit2Calc] = useState(
    chemical?.chemicalUnit2Calc?.toString() ?? ""
  );
  const [chemicalUnit3, setChemicalUnit3] = useState(chemical?.chemicalUnit3 ?? "");
  const [chemicalUnit3Calc, setChemicalUnit3Calc] = useState(
    chemical?.chemicalUnit3Calc?.toString() ?? ""
  );
  const [chemicalViewtype, setChemicalViewtype] = useState(chemical?.chemicalViewtype ?? 1);
  const [chemicalStorageCount, setChemicalStorageCount] = useState(
    chemical?.chemicalStorageCount ?? 1
  );
  const [chemicalIcon1, setchemicalIcon1] = useState(chemical?.chemicalIcon1);
  const [chemicalIcon2, setchemicalIcon2] = useState(chemical?.chemicalIcon2);
  const [selectedIcon1, setSelectedIcon1] = useState<IconOptions | null>(() => {
    const selectedIcon = iconOptions.find(icon => icon.key === chemicalIcon1);
    return selectedIcon || null;
  });
  const [selectedIcon2, setSelectedIcon2] = useState<IconOptions | null>(() => {
    const selectedIcon = iconOptions.find(icon => icon.key === chemicalIcon2);
    return selectedIcon || null;
  });

  const [chemicalIntensity, setChemicalIntensity] = useState(
    chemical?.chemicalIntensity?.toString() ?? ""
  );
  const [chemicalLimit, setChemicalLimit] = useState(chemical?.chemicalLimit?.toString() ?? "");
  const [chemicalBorder1, setChemicalBorder1] = useState(
    chemical?.chemicalBorder1?.toString() ?? ""
  );
  const [chemicalBorder2, setChemicalBorder2] = useState(
    chemical?.chemicalBorder2?.toString() ?? ""
  );
  const [chemicalOrdervolume, setChemicalOrdervolume] = useState(
    chemical?.chemicalOrdervolume?.toString() ?? ""
  );
  const [chemicalMinOrdervolume, setChemicalMinOrdervolume] = useState(
    chemical?.chemicalMinOrdervolume?.toString() ?? ""
  );

  const [chemicalStockLimits, setChemicalStockLimits] = useState<string[]>(
    chemical?.chemicalStockLimit ? chemical?.chemicalStockLimit.map(String) : []
  );
  const [chemicalStockBorder1, setChemicalStockBorder1] = useState<string[]>(
    chemical?.chemicalStockBorder1 ? chemical?.chemicalStockBorder1.map(String) : []
  );
  const [chemicalStockBorder2, setChemicalStockBorder2] = useState<string[]>(
    chemical?.chemicalStockBorder2 ? chemical?.chemicalStockBorder2.map(String) : []
  );

  const [chemicalCalc01, setChemicalCalc01] = useState(chemical?.chemicalCalc01?.toString() ?? "");
  const [chemicalCalctype, setChemicalCalctype] = useState(chemical?.chemicalCalctype ?? 1);
  const [chemicalCalc11, setChemicalCalc11] = useState(chemical?.chemicalCalc11?.toString() ?? "");
  const [chemicalCalc12, setChemicalCalc12] = useState(chemical?.chemicalCalc12?.toString() ?? "");
  const [chemicalCalc21, setChemicalCalc21] = useState(chemical?.chemicalCalc21?.toString() ?? "");
  const [chemicalCalc31, setChemicalCalc31] = useState(chemical?.chemicalCalc31?.toString() ?? "");
  const [chemicalCalc32, setChemicalCalc32] = useState(chemical?.chemicalCalc32?.toString() ?? "");
  const [chemicalCalc33, setChemicalCalc33] = useState(chemical?.chemicalCalc33?.toString() ?? "");

  // 選択肢の見た目をカスタムする関数
  const formatOptionLabel = (option: IconOptions, { context }: { context: "menu" | "value" }) => {
    // 選択後の枠（value）に表示する場合 ＝ アイコン画像のみ
    if (context === "value") {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={option.icon}
            alt={option.label}
            style={{ width: "24px", height: "24px", objectFit: "contain" }}
          />
        </div>
      );
    }

    // ドロップダウンメニュー（menu）の中に表示する場合 ＝ 画像 ＋ テキスト
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <img
          src={option.icon}
          alt={option.label}
          style={{ width: "24px", height: "24px", objectFit: "contain" }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  // フォーム送信
  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();

    // 必須入力チェック
    let hasInputError = false;
    if (
      [
        chemicalName,
        chemicalName1,
        chemicalName2,
        chemicalName3,
        chemicalCode,
        chemicalWbs,
        chemicalUnit1,
        chemicalSort1,
        chemicalSort2,
        chemicalSort3,
        chemicalIntensity,
        chemicalLimit,
        chemicalBorder1,
        chemicalBorder2,
        chemicalOrdervolume,
        chemicalMinOrdervolume,
      ].includes("")
    )
      hasInputError = true;

    if (
      chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK &&
      (chemicalStorageCount < 1 ||
        chemicalStockLimits.includes("") ||
        chemicalStockLimits.includes("0") ||
        chemicalStockBorder1.includes("") ||
        chemicalStockBorder1.includes("0") ||
        chemicalStockBorder2.includes("") ||
        chemicalStockBorder2.includes("0"))
    )
      hasInputError = true;

    if (chemicalUnit2 !== "" && chemicalUnit2Calc === "") hasInputError = true;
    if (chemicalUnit3 !== "" && chemicalUnit3Calc === "") hasInputError = true;
    if (chemicalCalctype === 1 && chemicalCalc11 === "") hasInputError = true;
    if (chemicalCalctype === 1 && chemicalCalc12 === "") hasInputError = true;
    if (chemicalCalctype === 2 && chemicalCalc21 === "") hasInputError = true;
    if (chemicalCalctype === 3 && chemicalCalc31 === "") hasInputError = true;
    if (chemicalCalctype === 3 && chemicalCalc32 === "") hasInputError = true;
    if (chemicalCalctype === 3 && chemicalCalc33 === "") hasInputError = true;

    if (hasInputError) {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }

    if (!vendors.find(v => v.vendorId === vendorId)) hasInputError = true;
    if (!types.find(t => t.chemicalTypeId === chemicalTypeId)) hasInputError = true;
    if (
      [
        chemicalSort1,
        chemicalSort2,
        chemicalSort3,
        chemicalUnit2 ? chemicalUnit2Calc : 0,
        chemicalUnit3 ? chemicalUnit3Calc : 0,
        chemicalIntensity,
        chemicalLimit,
        chemicalBorder1,
        chemicalBorder2,
        chemicalCalc01 ? chemicalCalc01 : 0,
        chemicalOrdervolume,
        chemicalMinOrdervolume,
        chemicalCalctype === 1 ? chemicalCalc11 : 0,
        chemicalCalctype === 1 ? chemicalCalc12 : 0,
        chemicalCalctype === 2 ? chemicalCalc21 : 0,
        chemicalCalctype === 3 ? chemicalCalc31 : 0,
        chemicalCalctype === 3 ? chemicalCalc32 : 0,
        chemicalCalctype === 3 ? chemicalCalc33 : 0,
      ].findIndex(v => Number.isNaN(Number(v))) >= 0
    )
      hasInputError = true;

    if (hasInputError) {
      showErrorMessage(MESSAGES.ERROR_INPUT_INVALID);
      return;
    }

    onRegister({
      chemicalId: chemical?.chemicalId,
      vendorId: vendorId ?? 0,
      additionalVendorId: additionalVendorIds,
      chemicalTypeId: chemicalTypeId ?? 0,
      chemicalName,
      chemicalName1,
      chemicalName2,
      chemicalName3,
      chemicalSort1: Number(chemicalSort1),
      chemicalSort2: Number(chemicalSort2),
      chemicalSort3: Number(chemicalSort3),
      chemicalCode,
      chemicalWbs,
      chemicalUnit1,
      chemicalUnit2,
      chemicalUnit2Calc: chemicalUnit2 ? Number(chemicalUnit2Calc) : 0,
      chemicalUnit3,
      chemicalUnit3Calc: chemicalUnit3 ? Number(chemicalUnit3Calc) : 0,
      chemicalViewtype,
      chemicalStorageCount: Number(chemicalStorageCount),
      chemicalIcon1,
      chemicalIcon2,
      chemicalIntensity: Number(chemicalIntensity),
      chemicalLimit: Number(chemicalLimit),
      chemicalBorder1: Number(chemicalBorder1),
      chemicalBorder2: Number(chemicalBorder2),
      chemicalStockLimit: chemicalStockLimits.map(value => Number(value)),
      chemicalStockBorder1: chemicalStockBorder1.map(value => Number(value)),
      chemicalStockBorder2: chemicalStockBorder2.map(value => Number(value)),
      chemicalOrdervolume: Number(chemicalOrdervolume),
      chemicalMinOrdervolume: Number(chemicalMinOrdervolume),
      chemicalCalc01: chemicalCalc01 ? Number(chemicalCalc01) : 0,
      chemicalCalctype,
      chemicalCalc11: chemicalCalctype === 1 ? Number(chemicalCalc11) : 0,
      chemicalCalc12: chemicalCalctype === 1 ? Number(chemicalCalc12) : 0,
      chemicalCalc21: chemicalCalctype === 2 ? Number(chemicalCalc21) : 0,
      chemicalCalc31: chemicalCalctype === 3 ? Number(chemicalCalc31) : 0,
      chemicalCalc32: chemicalCalctype === 3 ? Number(chemicalCalc32) : 0,
      chemicalCalc33: chemicalCalctype === 3 ? Number(chemicalCalc33) : 0,
    });
  };

  return (
    <div className="modal">
      <div className="content">
        <ModalHeader title="薬剤" onClose={onClose} />
        <form
          onSubmit={sendForm}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {/* 左の列 */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* 薬剤情報 */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>薬剤情報</legend>
              <div className="parts">
                <label>薬剤表示名：</label>
                <InputText val={chemicalName} onChange={setChemicalName} />
              </div>

              <div className="parts">
                <label>担当業者名：</label>
                <div className="vendor-row">
                  <select
                    className="vendor-select"
                    value={vendorId}
                    onChange={e => setVendorId(Number(e.target.value))}
                  >
                    {getVendorOptions(vendorId).map(v => (
                      <option key={v.vendorId} value={v.vendorId}>
                        {v.vendorName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="vendor-action add-vendor"
                    onClick={addVendor}
                    disabled={!canAddVendor}
                  >
                    ＋
                  </button>
                </div>
              </div>

              {additionalVendorIds.map((additionalVendorId, index) => (
                <div className="parts" key={index}>
                  <label>{`担当業者名${index + 2}：`}</label>
                  <div className="vendor-row">
                    <select
                      className="vendor-select"
                      value={additionalVendorId}
                      onChange={e =>
                        setAdditionalVendorIds(prev =>
                          prev.map((value, idx) =>
                            idx === index ? Number(e.target.value) : value
                          )
                        )
                      }
                    >
                      {getVendorOptions(additionalVendorId).map(v => (
                        <option key={v.vendorId} value={v.vendorId}>
                          {v.vendorName}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="vendor-action remove-vendor"
                      onClick={() => removeVendor(index)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}

              <div className="parts">
                <label>薬剤カテゴリ名：</label>
                <select
                  value={chemicalTypeId}
                  onChange={e => setChemicalTypeId(Number(e.target.value))}
                >
                  {types.map(t => (
                    <option key={t.chemicalTypeId} value={t.chemicalTypeId}>
                      {t.chemicalTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="parts">
                <label>表示順番：</label>
                <InputRealNumberText val={chemicalSort1} onChange={setChemicalSort1} />
              </div>

              <div className="parts">
                <label>管理用途：</label>
                <select
                  value={chemicalViewtype}
                  onChange={e => setChemicalViewtype(Number(e.target.value))}
                >
                  <option key="1" value="1">
                    貯槽系管理
                  </option>
                  <option key="2" value="2">
                    箱系管理
                  </option>
                  <option key="3" value="3">
                    箱系＋貯槽系管理
                  </option>
                </select>
              </div>

              {/* 管理用途が箱系＋貯槽系管理の場合は、保管貯槽数を設定 */}
              {chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK && (
                <div className="parts">
                  <label>保管貯槽数：</label>
                  <InputRealNumberText
                    val={chemicalStorageCount}
                    onChange={setChemicalStorageCount}
                    min={1}
                  />
                </div>
              )}

              <div className="parts">
                <label>アイコンイメージ：</label>
                <div className="icon-image-select">
                  <Select
                    value={selectedIcon1}
                    onChange={newValue => {
                      setSelectedIcon1(newValue);
                      setchemicalIcon1(newValue?.key);
                    }}
                    options={iconOptions}
                    formatOptionLabel={formatOptionLabel}
                    placeholder="1個目"
                    required
                  />
                </div>
                {/* 管理用途が箱系＋貯槽系管理の場合は、2個目のアイコンイメージを設定 */}
                {chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK && (
                  <div className="icon-image-select">
                    <Select
                      value={selectedIcon2}
                      onChange={newValue => {
                        setSelectedIcon2(newValue);
                        setchemicalIcon2(newValue?.key);
                      }}
                      options={iconOptions}
                      formatOptionLabel={formatOptionLabel}
                      placeholder="2個目"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="parts">
                <label>単位（残量）：</label>
                <InputText val={chemicalUnit1} onChange={setChemicalUnit1} />
              </div>

              <div className="parts">
                <label>単位（発注）：</label>
                <InputText val={chemicalUnit2} onChange={setChemicalUnit2} allowBlank />
              </div>

              {chemicalUnit2 !== "" && (
                <div className="parts">
                  <label>換算補正値：</label>
                  <InputRealNumberText val={chemicalUnit2Calc} onChange={setChemicalUnit2Calc} />
                </div>
              )}

              <div className="parts">
                <label>転記：</label>
                <InputText val={chemicalUnit3} onChange={setChemicalUnit3} allowBlank />
              </div>

              {chemicalUnit3 !== "" && (
                <div className="parts">
                  <label>転記補正値：</label>
                  <InputRealNumberText val={chemicalUnit3Calc} onChange={setChemicalUnit3Calc} />
                </div>
              )}
            </fieldset>

            {/* 納入実績表 */}
            <fieldset>
              <legend style={legendStyle}>納入実績表</legend>

              <div className="parts">
                <label>薬剤表示名：</label>
                <InputText val={chemicalName2} onChange={setChemicalName2} />
              </div>

              <div className="parts">
                <label>表示順番：</label>
                <InputRealNumberText val={chemicalSort2} onChange={setChemicalSort2} />
              </div>

              <div className="parts">
                <label>品目コード：</label>
                <InputText val={chemicalCode} onChange={setChemicalCode} />
              </div>

              <div className="parts">
                <label>WBS要素：</label>
                <InputText val={chemicalWbs} onChange={setChemicalWbs} />
              </div>
            </fieldset>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* 管理値 */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>管理値</legend>

              <div className="parts">
                <label>設計原単位：</label>
                <InputRealNumberText val={chemicalIntensity} onChange={setChemicalIntensity} />
              </div>

              <div className="parts">
                <label>在庫上限：</label>
                <InputRealNumberText val={chemicalLimit} onChange={setChemicalLimit} />
              </div>

              <div className="parts">
                <label>補給レベル：</label>
                <InputRealNumberText val={chemicalBorder1} onChange={setChemicalBorder1} />
              </div>

              <div className="parts">
                <label>発注レベル：</label>
                <InputRealNumberText val={chemicalBorder2} onChange={setChemicalBorder2} />
              </div>

              <div className="parts">
                <label>発注量：</label>
                <InputRealNumberText val={chemicalOrdervolume} onChange={setChemicalOrdervolume} />
              </div>

              <div className="parts">
                <label>最低発注量：</label>
                <InputRealNumberText
                  val={chemicalMinOrdervolume}
                  onChange={setChemicalMinOrdervolume}
                />
              </div>

              {/* 管理用途が箱系＋貯槽系管理の場合は、保管貯槽 在庫上限、保管貯槽 補給レベル、保管貯槽 発注レベルを設定 */}
              {chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK && (
                <>
                  {Array.from({ length: chemicalStorageCount }).map((_, index) => (
                    <div className="parts" key={index}>
                      <label htmlFor={`chemicalStockLimits${index}`}>
                        保管貯槽{index + 1} 在庫上限：
                      </label>
                      <input
                        type="number"
                        id={`chemicalStockLimits${index}`}
                        value={chemicalStockLimits[index] ?? ""}
                        onChange={e =>
                          setChemicalStockLimits(prev => {
                            const newChemicalStockLimits = [...(prev || [])];
                            newChemicalStockLimits[index] = e.target.value;
                            return newChemicalStockLimits;
                          })
                        }
                        required
                      />
                    </div>
                  ))}
                  {Array.from({ length: chemicalStorageCount }).map((_, index) => (
                    <div className="parts" key={index}>
                      <label htmlFor={`chemicalStockBorder1${index}`}>
                        保管貯槽{index + 1} 補給レベル：
                      </label>
                      <input
                        type="number"
                        id={`chemicalStockBorder1${index}`}
                        value={chemicalStockBorder1[index] ?? ""}
                        onChange={e =>
                          setChemicalStockBorder1(prev => {
                            const newChemicalStockBorder1 = [...(prev || [])];
                            newChemicalStockBorder1[index] = e.target.value;
                            return newChemicalStockBorder1;
                          })
                        }
                        required
                      />
                    </div>
                  ))}
                  {Array.from({ length: chemicalStorageCount }).map((_, index) => (
                    <div className="parts" key={index}>
                      <label htmlFor={`chemicalStockBorder2${index}`}>
                        保管貯槽{index + 1} 発注レベル：
                      </label>
                      <input
                        type="number"
                        id={`chemicalStockBorder2${index}`}
                        value={chemicalStockBorder2[index] ?? ""}
                        onChange={e =>
                          setChemicalStockBorder2(prev => {
                            const newChemicalStockBorder2 = [...(prev || [])];
                            newChemicalStockBorder2[index] = e.target.value;
                            return newChemicalStockBorder2;
                          })
                        }
                        required
                      />
                    </div>
                  ))}
                </>
              )}

              <div className="parts">
                <label>不燃粗大施設稼働時使用量：</label>
                <InputRealNumberText val={chemicalCalc01} onChange={setChemicalCalc01} />
              </div>

              <div className="parts">
                <label>使用量予測算出方法：</label>
                <select
                  value={chemicalCalctype}
                  onChange={e => setChemicalCalctype(Number(e.target.value))}
                >
                  <option key="1" value="1">
                    原単位で算出
                  </option>
                  <option key="2" value="2">
                    定期使用量で算出
                  </option>
                  <option key="3" value="3">
                    立上げ下げ回数で算出
                  </option>
                </select>
              </div>

              {chemicalCalctype === 1 && (
                <>
                  <div className="parts">
                    <label>(1)在庫下下限：</label>
                    <InputRealNumberText val={chemicalCalc11} onChange={setChemicalCalc11} />
                  </div>

                  <div className="parts">
                    <label>(1)共通停炉時使用量：</label>
                    <InputRealNumberText val={chemicalCalc12} onChange={setChemicalCalc12} />
                  </div>
                </>
              )}

              {chemicalCalctype === 2 && (
                <div className="parts">
                  <label>(2)定期使用量（1日）：</label>
                  <InputRealNumberText val={chemicalCalc21} onChange={setChemicalCalc21} />
                </div>
              )}

              {chemicalCalctype === 3 && (
                <>
                  <div className="parts">
                    <label>(3)立上げ時使用量：</label>
                    <InputRealNumberText val={chemicalCalc31} onChange={setChemicalCalc31} />
                  </div>

                  <div className="parts">
                    <label>(3)立下げ時使用量：</label>
                    <InputRealNumberText val={chemicalCalc32} onChange={setChemicalCalc32} />
                  </div>

                  <div className="parts">
                    <label>(3)非常用発電時使用量：</label>
                    <InputRealNumberText val={chemicalCalc33} onChange={setChemicalCalc33} />
                  </div>
                </>
              )}
            </fieldset>

            {/* 年間調達計画 */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>年間調達計画</legend>

              <div className="parts">
                <label>薬剤表示名：</label>
                <InputText val={chemicalName3} onChange={setChemicalName3} />
              </div>

              <div className="parts">
                <label>表示順番：</label>
                <InputRealNumberText val={chemicalSort3} onChange={setChemicalSort3} />
              </div>
            </fieldset>

            {/* 発注書 */}
            <fieldset>
              <legend style={legendStyle}>発注書</legend>

              <div className="parts">
                <label>薬剤表示名：</label>
                <InputText val={chemicalName1} onChange={setChemicalName1} />
              </div>
            </fieldset>
          </div>

          <div className="parts actions" style={{ gridColumn: "2 / 3" }}>
            <button type="button" onClick={onClose} className="negative">
              キャンセル
            </button>
            <button type="submit" className="positive">
              登録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChemicalModal;
